/**
 * One `<measure>` of one part, read into a bar of the document.
 *
 * MusicXML writes a bar as a cursor moving over a single timeline: notes advance it,
 * `<backup>` rewinds it so a second voice can be written from the start of the bar again,
 * and `<forward>` skips over time a voice does not fill. The document has no cursor -- a
 * voice is a list of events that fills the bar from its beginning -- so the reading here
 * records the tick each note starts at and rebuilds the lists afterwards, inserting rests
 * wherever the file left a gap. Doing it in one pass instead would mean guessing, at the
 * moment a `<forward>` appears, whether the gap belongs to the voice being written or to
 * one that has not started yet.
 */

import { createId } from "../document/ids";
import type { EventId } from "../document/ids";
import { plain } from "../document/duration";
import type { Clef, Voice } from "../document/types";
import { attr, numberOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";
import { fromDivisions } from "./common";
import {
  closeSpan,
  instrumentTaken,
  readAttributes,
  readBarline,
  readDirection,
} from "./import-bar";
import { noJumps, readJumpSound } from "./import-jumps";
import type { GlobalRead, MeasureRead, PartState, Walk } from "./import-bar";
import { readNote } from "./import-notes";
import { readFiguredBass } from "./import-figures";
import { readHarmony } from "./import-harmony";
import { buildVoice, eventOf } from "./import-values";
import type { VoiceBuild } from "./import-values";

/** Attach everything that was waiting for a note to the note that has just arrived. */
function flush(walk: Walk, state: PartState, id: EventId): void {
  for (const pending of walk.pendingDirections) {
    walk.directions.push({ id: createId("direction"), anchor: id, ...pending });
  }
  walk.pendingDirections.length = 0;

  for (const span of walk.pendingSpans) {
    state.open.set(`${span.pool}:${span.number}`, {
      kind: span.kind,
      number: span.number,
      from: id,
      placement: span.placement,
    });
  }
  walk.pendingSpans.length = 0;
}

function bucket(walk: Walk, number: number, staff: number): VoiceBuild {
  const existing = walk.voices.get(number);
  if (existing !== undefined) return existing;
  const fresh: VoiceBuild = { staff, placed: [] };
  walk.voices.set(number, fresh);
  return fresh;
}

function readNoteElement(element: XmlElement, walk: Walk, state: PartState): void {
  const read = readNote(element, state.divisions, state.drums);
  const drum = read.pitch?.drum ?? null;
  // The roster says what a drum sounds; only the note says where it is drawn.
  if (drum !== null && read.pitch !== null && !state.seen.has(drum)) {
    state.seen.set(drum, {
      step: read.pitch.step,
      octave: read.pitch.octave,
      notehead: read.notehead,
      open: read.open,
    });
  }
  const build = bucket(walk, read.voice, read.staff);

  if (read.chord) {
    const previous = build.placed[build.placed.length - 1];
    if (previous !== undefined && previous.event.kind === "note" && read.pitch !== null) {
      previous.event = { ...previous.event, pitches: [...previous.event.pitches, read.pitch] };
    }
  } else {
    const event = eventOf(read);
    // Which staff drew it is recorded rather than decided: whether it is the voice's own
    // is only known once the whole voice has been read. See `buildVoice`.
    build.placed.push({ onset: walk.cursor, ticks: read.ticks, staff: read.staff, event });
    walk.lastEvent = event.id;
    if (walk.cursor >= walk.latest) {
      state.last = event.id;
      walk.latest = walk.cursor;
    }
    flush(walk, state, event.id);
    walk.cursor += read.ticks;
    walk.filled = Math.max(walk.filled, walk.cursor);
  }

  const anchor = walk.lastEvent;
  if (anchor === null) return;
  for (const slur of read.slurs) {
    const key = `slur:${slur.number}`;
    if (slur.type === "start") {
      state.open.set(key, {
        kind: "slur",
        number: slur.number,
        from: anchor,
        placement: slur.placement,
      });
      continue;
    }
    closeSpan(state, key, anchor);
  }

  for (const gliss of read.glissandos) {
    const key = `gliss:${gliss.number}`;
    if (gliss.type === "start") {
      // A glissando has no side of the staff; the document asks for one anyway, and `above`
      // is what it is written back out as, which no reader draws differently.
      state.open.set(key, {
        kind: "glissando",
        number: gliss.number,
        from: anchor,
        placement: "above",
      });
      continue;
    }
    closeSpan(state, key, anchor);
  }
}

/** Read one bar of one part, advancing `state` with anything that outlives the bar. */
export function readMeasure(element: XmlElement, state: PartState): MeasureRead {
  const walk: Walk = {
    cursor: 0,
    filled: 0,
    voices: new Map(),
    directions: [],
    pendingDirections: [],
    pendingSpans: [],
    instrumentId: null,
    harmonies: [],
    figures: [],
    figureAhead: 0,
    lastEvent: null,
    latest: -1,
  };
  const clefs: Record<number, Clef> = {};
  const global: GlobalRead = {
    tempo: null,
    rehearsal: null,
    barline: null,
    repeatStart: false,
    repeatEnd: false,
    repeatTimes: null,
    endingStart: null,
    endingStop: false,
    jumps: noJumps(),
    breakBefore: null,
  };
  let key: MeasureRead["key"] = null;
  let time: MeasureRead["time"] = null;

  for (const node of element.children) {
    switch (node.name) {
      case "print":
        if (attr(node, "new-page") === "yes") global.breakBefore = "page";
        else if (attr(node, "new-system") === "yes") global.breakBefore ??= "system";
        break;
      case "attributes": {
        const stated = readAttributes(node, state, clefs);
        key ??= stated.key;
        time ??= stated.time;
        break;
      }
      case "direction":
        readDirection(node, walk, state, global);
        break;
      case "harmony": {
        // The cursor is where the reader stands, which is the start of the note about to
        // be written; the element's own offset carries it the rest of the way.
        const harmony = readHarmony(node, state.divisions, walk.cursor);
        if (harmony !== null) walk.harmonies.push(harmony);
        break;
      }
      case "figured-bass": {
        // Stacks before one note queue up: each begins where the one before it ended.
        const read = readFiguredBass(node, state.divisions, walk.cursor + walk.figureAhead);
        if (read.stack !== null) walk.figures.push(read.stack);
        walk.figureAhead += read.ticks;
        break;
      }
      case "note":
        readNoteElement(node, walk, state);
        walk.figureAhead = 0;
        break;
      case "backup":
        walk.cursor = Math.max(
          0,
          walk.cursor - fromDivisions(numberOf(node, "duration", 0), state.divisions),
        );
        break;
      case "forward":
        walk.cursor += fromDivisions(numberOf(node, "duration", 0), state.divisions);
        walk.filled = Math.max(walk.filled, walk.cursor);
        break;
      case "barline":
        readBarline(node, global);
        break;
      // A `<sound>` of its own, outside any direction, is where some writers put a jump
      // that is meant to be played and not printed.
      case "sound":
        readJumpSound(node, global.jumps);
        break;
      default:
        break;
    }
  }

  if (walk.lastEvent !== null) flush(walk, state, walk.lastEvent);

  const voices = [...walk.voices.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, build]) => buildVoice(build));

  return {
    measure: {
      voices: voices.length > 0 ? voices : [silentVoice()],
      instrument: instrumentTaken(state, walk.instrumentId),
      clefChanges: clefs,
      directions: walk.directions,
      harmonies: walk.harmonies,
      figures: walk.figures,
    },
    clefs,
    key,
    time,
    transposition: state.transposition,
    implicit: attr(element, "implicit") === "yes",
    filled: walk.filled,
    ...global,
  };
}

/** A bar a part says nothing about is a bar it is silent in. */
function silentVoice(): Voice {
  return {
    id: createId("voice"),
    staff: 0,
    ossia: false,
    events: [
      {
        id: createId("event"),
        kind: "rest",
        duration: plain("whole"),
        wholeMeasure: true,
        fermata: null,
        cue: false,
        offset: null,
        staff: null,
      },
    ],
  };
}
